import { PlusOutlined } from '@ant-design/icons'
import { Button, Divider, Flex, Form, Table, Tag } from 'antd'
import { MenuProps } from 'antd/lib'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { Dispatch, Key, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useInvestigationsServiceGetListSpecimensEndpoint } from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  Permissions,
  SpecimenItemDto,
  SpecimenLaboratoryStatus,
  SpecimenStatus
} from 'src/api/requests'
import { DocumentTextIcon } from 'src/assets'
import {
  ClassifierInfiniteSelect,
  PageBreadcrumbs,
  RangePicker,
  SearchBar,
  Select,
  TableActionButtons,
  Text
} from 'src/components'
import { UsersInfinitySelect } from 'src/features/activity/components'
import { ExternalResultModal } from 'src/features/persons/components/PersonInvestigations/ExternalResultModal'
import { getFullName } from 'src/features/users/utils'
import { useClassifierId, useTablePagination, useUser } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { resetPagination } from 'src/utils'

import {
  CancelInvestigationModal,
  ReceptionModal,
  RejectInvestigationModal,
  SpecimenInvestigationsExpandedRow,
  TestStatusTag,
  ValidateInvestigationModal,
  LaboratoryStatusTag
} from '../components'

import { useStyles } from './styles'

enum ModalTypeEnum {
  DELETE = 'delete',
  DOCTOR = 'doctor',
  RECEPTION = 'reception',
  VALIDATE = 'validate',
  REJECT = 'reject'
}

export const TestsListPage = () => {
  const { styles } = useStyles()
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const { userData, hasPermission } = useUser()

  const pagination = useTablePagination()

  const hasUsersAccess = hasPermission([
    Permissions.IDENTITY_USERS_VIEW_ALL,
    Permissions.IDENTITY_USERS_VIEW_INSTITUTION
  ])

  const isAssistant = hasPermission(Permissions.INVESTIGATIONS_SPECIMENS_UPDATE)

  const [currentId, setCurrentId] = useState<string>()
  const [modalType, setModalType] = useState<ModalTypeEnum>()
  const [isExternalOpen, setIsExternalOpen] = useState(false)

  const [search, setSearch] = useState('')
  const [createdAtFrom, setCreatedAtFrom] = useState('')
  const [createdAtTo, setCreatedAtTo] = useState('')
  const [institutionUserId, setInstitutionUserId] = useState('')
  const [status, setStatus] = useState<string>()
  const [typeId, setTypeId] = useState<string>()
  const [laboratoryStatus, setLaboratoryStatus] = useState<string>()
  const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>([])

  const [form] = Form.useForm()

  const specimenTypeClassifierId = useClassifierId(
    ClassifierType.SPECIMEN_TYPE,
    ClassifierCategory.INVESTIGATIONS
  )

  const institutionId = String(userData?.currentLegalEntityId)

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    institutionIds: [institutionId],
    ...(!!search && {
      searchTerm: search
    }),
    ...(createdAtFrom &&
      createdAtTo && {
        createdAtFrom,
        createdAtTo
      }),
    ...(institutionUserId && { institutionUserId }),
    ...(typeId && { typeIds: [typeId] }),
    ...(laboratoryStatus && {
      laboratoryStatus: [laboratoryStatus as SpecimenLaboratoryStatus]
    }),
    status: status
      ? [status as SpecimenStatus]
      : [
          SpecimenStatus.HARVESTED,
          SpecimenStatus.SENT_TO_LABORATORY,
          SpecimenStatus.RECEIVED,
          SpecimenStatus.IN_PROGRESS,
          SpecimenStatus.REJECTED,
          SpecimenStatus.COMPLETED
        ]
  }

  const { data, isLoading } =
    useInvestigationsServiceGetListSpecimensEndpoint(queryParams)

  const onFilterChange = (val: string, setState: Dispatch<string>) => {
    setState(val)
    resetPagination(pagination)
  }

  const onStatusChange = (val: string) => {
    setStatus(val)
    resetPagination(pagination)
  }

  const onTypeChange = (val?: string) => {
    setTypeId(val)
    resetPagination(pagination)
  }

  const onLaboratoryStatusChange = (val?: string) => {
    setLaboratoryStatus(val)
    resetPagination(pagination)
  }

  const onCloseModal = () => {
    setCurrentId(undefined)
    setModalType(undefined)
    form.resetFields()
  }

  const getActionItems = useMemo(
    () =>
      (
        id: string,
        laboratoryStatus: SpecimenLaboratoryStatus,
        status: SpecimenStatus
      ): MenuProps['items'] => {
        const isSpecimenValidated = [
          SpecimenLaboratoryStatus.VALIDATED,
          SpecimenLaboratoryStatus.REJECTED
        ].includes(laboratoryStatus)

        if (isAssistant && status === SpecimenStatus.SENT_TO_LABORATORY) {
          return [
            {
              key: 'reception',
              label: t('investigations.reception'),
              onClick: () => {
                setCurrentId(id)
                setModalType(ModalTypeEnum.RECEPTION)
              }
            }
          ]
        }

        if (isSpecimenValidated && !isAssistant)
          return [
            {
              key: 'details',
              label: t('investigations.investigation_details'),
              icon: <DocumentTextIcon />,
              onClick: () => navigate(paths.tests.specimenDetails(id))
            }
          ]

        if (!isAssistant)
          return [
            {
              key: 'validate',
              label: t('investigations.validate_investigation'),
              onClick: () => {
                setCurrentId(id)
                setModalType(ModalTypeEnum.VALIDATE)
              }
            },
            {
              key: 'divider',
              type: 'divider'
            },
            {
              key: 'reject',
              label: t('investigations.reject'),
              danger: true,
              onClick: () => {
                setCurrentId(id)
                setModalType(ModalTypeEnum.REJECT)
              }
            }
          ]

        return []
      },
    [t]
  )

  // A row still needs action from the current user: assistants receive
  // specimens arriving from the doctor; validators handle any specimen not yet
  // validated/rejected. Mirrors the action-menu logic in getActionItems.
  const needsProcessing = ({ status }: SpecimenItemDto) => {
    if (isAssistant) return status === SpecimenStatus.SENT_TO_LABORATORY

    return (
      status !== SpecimenStatus.COMPLETED && status !== SpecimenStatus.REJECTED
    )
  }

  const tableColumns: ColumnsType<SpecimenItemDto> = [
    {
      key: 'harvest date',
      title: t('investigations.harvest_date'),
      render: (_, { harvestDate }) =>
        harvestDate
          ? dayjs
              .utc(harvestDate)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'performed at',
      title: t('investigations.performed_datetime'),
      render: (_, { createdAt }) =>
        createdAt
          ? dayjs
              .utc(createdAt)
              .tz()
              .format(dayMonthYearHourMinutesFormatWithDot)
          : '-'
    },
    {
      key: 'code',
      title: t('tests.code'),
      dataIndex: 'code'
    },
    {
      key: 'patient',
      title: t('common.patient'),
      render: (_, { person }) => {
        if (!person) return '-'

        if (person.isAnonymous)
          return <Tag>{t('investigations.anonymous')}</Tag>

        const fullName = getFullName(
          person.firstName ?? undefined,
          person.lastName ?? undefined
        )
        const code = person.simeCode ?? person.tarvCode

        return (
          <Flex vertical>
            <span>{fullName || '-'}</span>
            {code && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {code}
              </Text>
            )}
          </Flex>
        )
      }
    },
    ...(!isAssistant
      ? [
          {
            key: 'internal code',
            title: t('investigations.internal_code'),
            render: (_: unknown, { laboratoryInternalCode }: SpecimenItemDto) =>
              laboratoryInternalCode ?? '-'
          }
        ]
      : []),
    {
      key: 'specimen type',
      title: t('consultations.specimen_type'),
      render: (_, { type }) => type?.translation?.[currentLang.value] ?? '-'
    },
    {
      key: 'categories',
      title: t('investigations.categories'),
      render: (_, { demand }) => {
        const categories = demand?.categories ?? []

        if (!categories.length) return '-'

        return (
          <Flex gap={4} wrap="wrap">
            {categories.map((category) => (
              <Tag key={category.id}>
                {category.translation?.[currentLang.value] ?? '-'}
              </Tag>
            ))}
          </Flex>
        )
      }
    },
    {
      key: 'investigation reason',
      title: t('investigations.investigation_reason'),
      render: (_, { demand }) => {
        const reasons = [
          demand?.investigationReason,
          ...(demand?.secondaryInvestigationReasons ?? [])
        ].filter(Boolean)

        if (!reasons.length) return '-'

        return (
          <Flex vertical gap={4} align="start">
            {reasons.map((reason) => (
              <Tag key={reason?.id}>
                {reason?.translation?.[currentLang.value] ?? '-'}
              </Tag>
            ))}
          </Flex>
        )
      }
    },

    ...(!isAssistant
      ? [
          {
            key: 'laboratoryStatus',
            title: t('investigations.investigation_status'),
            render: (_: unknown, record: SpecimenItemDto) => (
              <LaboratoryStatusTag
                status={record?.laboratoryStatus}
                testStatus={record?.status}
              />
            )
          }
        ]
      : []),

    {
      key: 'progress',
      title: t('form.progress'),
      render: (_: unknown, { status }: SpecimenItemDto) => (
        <TestStatusTag status={status} />
      )
    },
    {
      key: 'actions',
      fixed: 'right',
      render: ({ id, laboratoryStatus, status }) => (
        <TableActionButtons
          justify="center"
          items={getActionItems(id, laboratoryStatus, status)}
        />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      title={t('menu.tests')}
      items={[{ title: t('menu.tests') }]}
    >
      <Flex gap={8} wrap="wrap">
        <SearchBar
          onSearch={(val: string) => onFilterChange(val, setSearch)}
          className={styles.search}
        />

        {hasUsersAccess && (
          <UsersInfinitySelect
            onClear={() => onFilterChange('', setInstitutionUserId)}
            onSelect={(val: string) =>
              onFilterChange(val, setInstitutionUserId)
            }
            placeholder={t('common.doctor')}
            popupMatchSelectWidth={false}
            dropdownAlign={{ points: ['t', 'b'] }}
            style={{ minWidth: 200 }}
            dropdownStyle={{ minWidth: 300 }}
          />
        )}

        <RangePicker
          format={dayMonthYearFormatWithDot}
          setDateTo={setCreatedAtTo}
          setDateFrom={setCreatedAtFrom}
          resetPagination={() => resetPagination(pagination)}
          placeholder={[
            t('tests.created_from_date'),
            t('tests.created_to_date')
          ]}
        />

        <ClassifierInfiniteSelect
          id={specimenTypeClassifierId}
          value={typeId}
          onChange={onTypeChange}
          onClear={() => onTypeChange(undefined)}
          allowClear
          placeholder={t('consultations.specimen_type')}
          style={{ minWidth: 200 }}
        />

        <Select
          options={Object.values(SpecimenStatus).map((status) => ({
            label: t(`tests.statuses.${status}`),
            value: status
          }))}
          onChange={onStatusChange}
          onClear={() => onStatusChange('')}
          allowClear
          placeholder={t('form.status')}
        />

        {!isAssistant && (
          <Select
            options={Object.values(SpecimenLaboratoryStatus).map((value) => ({
              label: t(`tests.laboratory_statuses.${value}`),
              value
            }))}
            onChange={onLaboratoryStatusChange}
            onClear={() => onLaboratoryStatusChange(undefined)}
            allowClear
            placeholder={t('investigations.investigation_status')}
          />
        )}

        {isAssistant && (
          <Button type="primary" onClick={() => setIsExternalOpen(true)}>
            <PlusOutlined />
            {t('persons.external_result.add')}
          </Button>
        )}
      </Flex>

      <Divider />

      <Table
        rowKey="id"
        scroll={{ x: 'max-content' }}
        dataSource={data?.data}
        loading={isLoading}
        columns={tableColumns}
        rowClassName={(record) =>
          needsProcessing(record) ? styles.needsProcessingRow : ''
        }
        pagination={{ ...pagination, total: data?.totalCount }}
        expandable={{
          expandedRowKeys,
          onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
          expandedRowRender: (record) => (
            <SpecimenInvestigationsExpandedRow
              specimenId={record.id}
              institutionId={institutionId}
              showLaboratoryStatus={!isAssistant}
            />
          )
        }}
      />

      {modalType === ModalTypeEnum.DELETE && (
        <CancelInvestigationModal
          form={form}
          onCancel={onCloseModal}
          onConfirm={() => {}}
        />
      )}

      {modalType === ModalTypeEnum.RECEPTION && (
        <ReceptionModal form={form} id={currentId} onCancel={onCloseModal} />
      )}

      {modalType === ModalTypeEnum.VALIDATE && (
        <ValidateInvestigationModal id={currentId} onCancel={onCloseModal} />
      )}

      {modalType === ModalTypeEnum.REJECT && (
        <RejectInvestigationModal id={currentId} onCancel={onCloseModal} />
      )}

      {isExternalOpen && (
        <ExternalResultModal onCancel={() => setIsExternalOpen(false)} />
      )}
    </PageBreadcrumbs>
  )
}
